<template>
    <div v-if="shouldShow" class="ai-assistant">
        <button
            v-if="!visible"
            type="button"
            class="ai-assistant__trigger"
            aria-label="Open AI Assistant"
            @click="openDialog"
        >
            <span class="ai-assistant__trigger-icon">
                <robot-outlined />
            </span>
        </button>

        <transition name="ai-assistant-fade">
            <div
                v-if="visible"
                :class="isFullscreen ? 'ai-assistant__workspace' : 'ai-assistant__panel'"
            >
                <div :class="isFullscreen ? 'ai-assistant__workspace-bar' : 'ai-assistant__header'">
                    <div class="ai-assistant__title-wrap">
                        <span class="ai-assistant__title-icon">
                            <robot-outlined />
                        </span>
                        <div class="ai-assistant__title">
                            <div class="ai-assistant__title-main">{{ title }}</div>
                            <div class="ai-assistant__title-sub">
                                {{ isFullscreen ? '全屏智能问答工作区' : '智能问答工作台' }}
                            </div>
                        </div>
                    </div>
                    <div :class="isFullscreen ? 'ai-assistant__workspace-actions' : 'ai-assistant__header-actions'">
                        <button
                            type="button"
                            class="ai-assistant__icon-btn"
                            :aria-label="isFullscreen ? 'Exit fullscreen' : 'Enter fullscreen'"
                            @click="toggleFullscreen"
                        >
                            <fullscreen-exit-outlined v-if="isFullscreen" />
                            <fullscreen-outlined v-else />
                        </button>
                        <button
                            type="button"
                            class="ai-assistant__icon-btn"
                            aria-label="Close AI Assistant"
                            @click="closeDialog"
                        >
                            <close-outlined />
                        </button>
                    </div>
                </div>
                <div :class="isFullscreen ? 'ai-assistant__workspace-body' : 'ai-assistant__body'">
                    <div
                        class="ai-assistant__iframe-shell"
                        :class="{ 'ai-assistant__iframe-shell--full': isFullscreen }"
                    >
                        <a-spin :spinning="frameLoading" tip="正在加载页面...">
                            <iframe
                                class="ai-assistant__iframe"
                                :src="assistantSrc"
                                :title="title"
                                frameborder="0"
                                @load="handleFrameLoad"
                            ></iframe>
                        </a-spin>
                    </div>
                </div>
            </div>
        </transition>
    </div>
</template>

<script>
import {
    CloseOutlined,
    FullscreenExitOutlined,
    FullscreenOutlined,
    RobotOutlined
} from '@ant-design/icons-vue'
import { mapGetters } from 'vuex'
import { getToken } from '@/utils/auth'

export default {
    name: 'AiAssistant',
    components: {
        CloseOutlined,
        FullscreenExitOutlined,
        FullscreenOutlined,
        RobotOutlined
    },
    props: {
        title: {
            type: String,
            default: 'AI 助手'
        },
        assistantUrl: {
            type: String,
            default: 'https://ai-audit-prod.qd-ecs.com:18180/embed/assistant'
        },
        hiddenPaths: {
            type: Array,
            default: () => ['/login']
        }
    },
    data() {
        return {
            visible: false,
            isFullscreen: false,
            frameLoading: false
        }
    },
    computed: {
        ...mapGetters(['user']),
        shouldShow() {
            return !this.hiddenPaths.includes(this.$route.path)
        },
        assistantUserId() {
            return this.user?.userid || this.user?.member?.member?.userid || this.user?.member?.userid || ''
        },
        assistantUserNo() {
            return this.user?.userlName || this.user?.member?.member?.userNO || ''
        },
        assistantENo() {
            return this.user?.eNo || this.user?.member?.eno || ''
        },
        assistantToken() {
            return (
                this.$store?.state?.user?.token ||
                getToken() ||
                this.$route?.query?.access_token ||
                localStorage.getItem('token') ||
                localStorage.getItem('Web-Token') ||
                ''
            )
        },
        assistantExpertId() {
            return 'b64924a3-018f-4dd7-aea2-5c860d1883bf'
        },
        assistantSrc() {
            const params = {
                userid: this.assistantUserId,
                userNo: this.assistantUserNo,
                eNo: this.assistantENo,
                token: this.assistantToken,
                expertId: this.assistantExpertId
            }
            try {
                const url = new URL(this.assistantUrl)
                Object.keys(params).forEach(key => {
                    if (params[key]) {
                        url.searchParams.set(key, params[key])
                    }
                })
                return url.toString()
            } catch (error) {
                const query = Object.keys(params)
                    .filter(key => params[key])
                    .map(key => `${key}=${encodeURIComponent(params[key])}`)
                    .join('&')
                if (!query) {
                    return this.assistantUrl
                }
                return `${this.assistantUrl}${this.assistantUrl.includes('?') ? '&' : '?'}${query}`
            }
        }
    },
    watch: {
        '$route.path'() {
            if (!this.shouldShow) {
                this.visible = false
                this.isFullscreen = false
            }
        },
        assistantSrc() {
            if (this.visible) {
                this.frameLoading = true
            }
        }
    },
    methods: {
        openDialog() {
            this.frameLoading = true
            this.isFullscreen = false
            this.visible = true
        },
        closeDialog() {
            this.visible = false
            this.isFullscreen = false
        },
        toggleFullscreen() {
            this.isFullscreen = !this.isFullscreen
        },
        handleFrameLoad() {
            this.frameLoading = false
        }
    }
}
</script>

<style scoped lang="less">
.ai-assistant {
    --assistant-right: 24px;
    --assistant-bottom: 20px;
    --assistant-top: 55px;

    position: fixed;
    right: var(--assistant-right);
    bottom: var(--assistant-bottom);
    z-index: 999;

    &__trigger {
        width: 56px;
        height: 56px;
        border: 1px solid #f2f3f5;
        border-radius: 14px;
        background: #ffffff;
        box-shadow: 0px 4px 10.56px 0.48px rgba(216, 216, 216, 0.4);
        color: #333333;
        display: flex;
        align-items: center;
        justify-content: center;
        padding: 0;
        cursor: pointer;
        transition: transform 0.2s ease, box-shadow 0.2s ease;

        &:hover {
            transform: translateY(-2px);
            box-shadow: 0px 1px 23px 0.9px rgba(216, 216, 216, 0.6);
        }
    }

    &__trigger-icon {
        width: 36px;
        height: 36px;
        border-radius: 10px;
        background: #4977fc;
        color: #ffffff;
        display: inline-flex;
        align-items: center;
        justify-content: center;
        font-size: 18px;
        flex-shrink: 0;
        box-shadow: 0 6px 14px rgba(73, 119, 252, 0.24);
    }

    &__panel {
        position: absolute;
        right: 0;
        bottom: 72px;
        width: 400px;
        height: calc(100vh - 150px);
        background: #ffffff;
        border: 1px solid #f2f3f5;
        border-radius: 12px;
        overflow: hidden;
        box-shadow: 0px 4px 10.56px 0.48px rgba(216, 216, 216, 0.4);
        display: flex;
        flex-direction: column;
    }

    &__header,
    &__workspace-bar {
        background: #ffffff;
        display: flex;
        align-items: center;
        justify-content: space-between;
        border-bottom: 1px solid #eef1f5;
    }

    &__header {
        padding: 18px 18px 14px;
    }

    &__workspace {
        position: fixed;
        top: var(--assistant-top);
        left: 0;
        right: 0;
        bottom: 0;
        z-index: 998;
        background: #f7f9fc;
        display: flex;
        flex-direction: column;
        border-top: 1px solid #eef1f5;
    }

    &__workspace-bar {
        height: 72px;
        padding: 0 24px;
        box-shadow: 0px 4px 10.56px 0.48px rgba(216, 216, 216, 0.18);
    }

    &__title-wrap {
        display: flex;
        align-items: center;
        min-width: 0;
    }

    &__title-icon {
        width: 42px;
        height: 42px;
        border-radius: 10px;
        background: #eff4ff;
        color: #4977fc;
        display: inline-flex;
        align-items: center;
        justify-content: center;
        font-size: 19px;
        flex-shrink: 0;
    }

    &__title {
        margin-left: 12px;
        min-width: 0;
        text-align: left;
    }

    &__title-main {
        font-size: 16px;
        line-height: 22px;
        font-weight: 700;
        color: #333333;
    }

    &__title-sub {
        margin-top: 2px;
        font-size: 12px;
        line-height: 18px;
        color: #999999;
    }

    &__header-actions,
    &__workspace-actions {
        display: flex;
        align-items: center;
        gap: 8px;
        min-width: 0;
        margin-left: 16px;
    }

    &__workspace-actions {
        gap: 12px;
    }

    &__icon-btn {
        width: 32px;
        height: 32px;
        border: 1px solid #e8edf5;
        border-radius: 8px;
        background: #ffffff;
        color: #4977fc;
        display: inline-flex;
        align-items: center;
        justify-content: center;
        cursor: pointer;
        transition: all 0.2s ease;

        &:hover {
            border-color: #4977fc;
            background: #eff4ff;
        }
    }

    &__tip {
        margin: 14px 18px 0;
        padding: 10px 12px;
        border-radius: 8px;
        background: #f5f8ff;
        border: 1px solid #e3edff;
        display: flex;
        align-items: flex-start;
        gap: 10px;
        text-align: left;
    }

    &__tip-badge {
        height: 22px;
        line-height: 22px;
        padding: 0 8px;
        border-radius: 4px;
        background: #4977fc;
        color: #ffffff;
        font-size: 11px;
        white-space: nowrap;
    }

    &__tip-text {
        font-size: 12px;
        line-height: 18px;
        color: #666666;
        max-width: 520px;
        overflow: hidden;
        text-overflow: ellipsis;
        white-space: nowrap;
    }

    &__body,
    &__workspace-body {
        flex: 1;
        min-height: 0;
    }

    &__body {
        padding: 14px 18px 18px;
        background: #ffffff;
    }

    &__workspace-body {
        padding: 18px 20px 20px;
        background: #f7f9fc;
    }

    &__iframe-shell {
        width: 100%;
        height: 100%;
        border: 1px solid #eef1f5;
        border-radius: 12px;
        overflow: hidden;
        background: #ffffff;
        box-shadow: 0px 4px 10.56px 0.48px rgba(216, 216, 216, 0.24);

        :deep(.ant-spin-nested-loading),
        :deep(.ant-spin-container) {
            width: 100%;
            height: 100%;
        }
    }

    &__iframe-shell--full {
        border-radius: 12px;
    }

    &__iframe {
        width: 100%;
        height: 100%;
        border: 0;
        display: block;
        background: #ffffff;
    }
}

.ai-assistant-fade-enter-active,
.ai-assistant-fade-leave-active {
    transition: opacity 0.2s ease, transform 0.2s ease;
}

.ai-assistant-fade-enter-from,
.ai-assistant-fade-leave-to {
    opacity: 0;
    transform: translateY(12px);
}

@media screen and (max-width: 768px) {
    .ai-assistant {
        --assistant-right: 12px;
        --assistant-bottom: 20px;
        --assistant-top: 55px;

        &__panel {
            width: min(400px, calc(100vw - 24px));
            height: calc(100vh - 84px);
            max-height: 760px;
            bottom: 72px;
        }

        &__workspace-bar {
            height: 64px;
            padding: 0 12px;
        }

        &__workspace-body {
            padding: 12px;
        }

        &__workspace-actions {
            gap: 8px;
            margin-left: 10px;
        }

        &__title-sub,
        &__tip-text {
            display: none;
        }
    }
}
</style>
